// src/components/auth/registration/AddressStep.jsx
import React, { useState, useEffect, useRef } from 'react';
import Input from '../../common/Input';
import Button from '../../common/Button';

const AddressStep = ({ data, updateData, nextStep, prevStep }) => {
  const [errors, setErrors] = useState({});
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState('');
  
  // Form data
  const [street, setStreet] = useState(data.street || '');
  const [city, setCity] = useState(data.city || '');
  const [state, setState] = useState(data.state || '');
  const [zip, setZip] = useState(data.zip || '');
  const [location, setLocation] = useState(data.location || null);
  const [fullAddress, setFullAddress] = useState(data.fullAddress || '');
  
  // Google Maps autocomplete
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [googleMapsApi, setGoogleMapsApi] = useState(null);
  
  // Refs for cleanup
  const mapInstance = useRef(null);
  const markerInstance = useRef(null);
  const autocompleteInstance = useRef(null);
  const watchId = useRef(null);
  
  // Add state for device capabilities
  const [capabilities, setCapabilities] = useState(null);
  
  // Load Google Maps API
  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      setGoogleMapsApi(window.google.maps);
      return;
    }

    // Load Google Maps API
    const googleMapsScript = document.createElement('script');
    googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD590z__itIHB85Rrz0XJxEpi-PVYPs2b0&libraries=places`;
    googleMapsScript.async = true;
    googleMapsScript.defer = true;
    
    googleMapsScript.onload = () => {
      setMapLoaded(true);
      setGoogleMapsApi(window.google.maps);
    };

    googleMapsScript.onerror = () => {
      console.error('Failed to load Google Maps API');
      setLocationError('Failed to load Google Maps. You can still enter your address manually.');
    };
    
    // Only append if not already exists
    if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
      document.head.appendChild(googleMapsScript);
    }
    
    return () => {
      // Cleanup on unmount
      if (watchId.current) {
        navigator.geolocation.clearWatch(watchId.current);
      }
    };
  }, []);

  // Helper function to update marker position
  const updateMarkerPosition = (position, map) => {
    if (!googleMapsApi) return;
    
    if (markerInstance.current) {
      markerInstance.current.setPosition(position);
    } else {
      markerInstance.current = new googleMapsApi.Marker({
        position: position,
        map: map,
        draggable: true,
      });
      
      // Add drag listener to new marker
      googleMapsApi.event.addListener(markerInstance.current, 'dragend', function() {
        const markerPosition = markerInstance.current.getPosition();
        const draggedLocation = { 
          latitude: markerPosition.lat(), 
          longitude: markerPosition.lng() 
        };
        setLocation(draggedLocation);
        reverseGeocode(draggedLocation);
      });
    }
  };

  // Add current location control to map
  const addCurrentLocationControl = (map) => {
    if (!googleMapsApi) return;
    
    // Create the control container
    const controlDiv = document.createElement('div');
    controlDiv.style.margin = '10px';
    
    // Create the control button
    const controlUI = document.createElement('div');
    controlUI.style.backgroundColor = '#fff';
    controlUI.style.border = '2px solid #fff';
    controlUI.style.borderRadius = '3px';
    controlUI.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
    controlUI.style.cursor = 'pointer';
    controlUI.style.textAlign = 'center';
    controlUI.style.width = '40px';
    controlUI.style.height = '40px';
    controlUI.style.display = 'flex';
    controlUI.style.alignItems = 'center';
    controlUI.style.justifyContent = 'center';
    controlUI.title = 'Click to recenter the map on your location';
    controlDiv.appendChild(controlUI);
    
    // Create the control icon
    const controlIcon = document.createElement('div');
    controlIcon.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="3" stroke="#666" stroke-width="2" fill="none"/>
        <path d="M12 1v6m0 8v6m11-7h-6m-8 0H1" stroke="#666" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
    controlUI.appendChild(controlIcon);
    
    // Add hover effects
    controlUI.addEventListener('mouseenter', () => {
      controlUI.style.backgroundColor = '#f5f5f5';
    });
    
    controlUI.addEventListener('mouseleave', () => {
      controlUI.style.backgroundColor = '#fff';
    });
    
    // Add click handler
    controlUI.addEventListener('click', () => {
      // Visual feedback - change icon color
      controlIcon.style.opacity = '0.5';
      setTimeout(() => {
        controlIcon.style.opacity = '1';
      }, 200);
      
      // Get current location
      getCurrentLocation();
    });
    
    // Add the control to the map
    map.controls[googleMapsApi.ControlPosition.RIGHT_BOTTOM].push(controlDiv);
  };
  
  // Initialize autocomplete and map
  useEffect(() => {
    if (mapLoaded && googleMapsApi && mapRef.current) {
      try {
        // Initialize map
        const map = new googleMapsApi.Map(mapRef.current, {
          center: location 
            ? { lat: location.latitude, lng: location.longitude } 
            : { lat: 20.5937, lng: 78.9629 }, // Default to center of India
          zoom: 15,
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: googleMapsApi.MapTypeControlStyle.HORIZONTAL_BAR,
            position: googleMapsApi.ControlPosition.TOP_CENTER,
          },
          zoomControl: true,
          zoomControlOptions: {
            position: googleMapsApi.ControlPosition.RIGHT_CENTER,
          },
          streetViewControl: true,
          streetViewControlOptions: {
            position: googleMapsApi.ControlPosition.RIGHT_CENTER,
          },
          fullscreenControl: true,
        });
        
        mapInstance.current = map;
        
        // Add custom current location control
        addCurrentLocationControl(map);
        
        // Initialize autocomplete if input exists
        if (autocompleteRef.current) {
          autocompleteInstance.current = new googleMapsApi.places.Autocomplete(
            autocompleteRef.current,
            {
              componentRestrictions: { country: 'in' }, // Restrict to India
              fields: ['address_components', 'formatted_address', 'geometry']
            }
          );
          
          autocompleteInstance.current.addListener('place_changed', () => {
            const place = autocompleteInstance.current.getPlace();
            
            if (!place.geometry) {
              setLocationError('Please select a valid address from the dropdown.');
              return;
            }
            
            handlePlaceSelection(place, map);
          });
        }
        
        // Allow clicking on map to place marker
        map.addListener('click', (event) => {
          const newLocation = { 
            latitude: event.latLng.lat(), 
            longitude: event.latLng.lng() 
          };
          
          updateMarkerPosition(event.latLng, map);
          setLocation(newLocation);
          reverseGeocode(newLocation);
        });
        
      } catch (error) {
        console.error('Error initializing map:', error);
        setLocationError('Error initializing map. You can still enter your address manually.');
      }
    }
  }, [mapLoaded, googleMapsApi]);

  // Handle place selection from autocomplete
  const handlePlaceSelection = (place, map) => {
    try {
      // Extract address components
      const addressComponents = place.address_components || [];
      let newStreet = '', newCity = '', newState = '', newZip = '';
      
      addressComponents.forEach((component) => {
        const types = component.types;
        
        if (types.includes('street_number')) {
          newStreet = component.long_name + ' ';
        } else if (types.includes('route')) {
          newStreet += component.long_name;
        } else if (types.includes('locality') || types.includes('administrative_area_level_2')) {
          newCity = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          newState = component.long_name;
        } else if (types.includes('postal_code')) {
          newZip = component.long_name;
        }
      });
      
      // Update form fields
      setStreet(newStreet.trim());
      setCity(newCity);
      setState(newState);
      setZip(newZip);
      setFullAddress(place.formatted_address);
      
      // Update map and location
      const newLocation = {
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng()
      };
      setLocation(newLocation);
      
      // Update map center and marker
      map.setCenter(place.geometry.location);
      updateMarkerPosition(place.geometry.location, map);
      
      // Clear any previous errors
      setLocationError('');
      setErrors(prev => ({ ...prev, location: '' }));
      
    } catch (error) {
      console.error('Error handling place selection:', error);
      setLocationError('Error processing selected location.');
    }
  };

  // Reverse geocode coordinates to get address
  const reverseGeocode = async (coordinates) => {
    if (!googleMapsApi || !coordinates) return;
    
    try {
      const geocoder = new googleMapsApi.Geocoder();
      const latlng = { lat: coordinates.latitude, lng: coordinates.longitude };
      
      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const place = results[0];
          
          // Extract address components
          const addressComponents = place.address_components || [];
          let newStreet = '', newCity = '', newState = '', newZip = '';
          
          addressComponents.forEach((component) => {
            const types = component.types;
            
            if (types.includes('street_number')) {
              newStreet = component.long_name + ' ';
            } else if (types.includes('route')) {
              newStreet += component.long_name;
            } else if (types.includes('locality') || types.includes('administrative_area_level_2')) {
              newCity = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              newState = component.long_name;
            } else if (types.includes('postal_code')) {
              newZip = component.long_name;
            }
          });
          
          // Update form fields
          setStreet(newStreet.trim());
          setCity(newCity);
          setState(newState);
          setZip(newZip);
          setFullAddress(place.formatted_address);
          
          // Update autocomplete input
          if (autocompleteRef.current) {
            autocompleteRef.current.value = place.formatted_address;
          }
        } else {
          console.warn('Reverse geocoding failed:', status);
          // Set fallback address with coordinates
          setFullAddress(`${coordinates.latitude.toFixed(6)}, ${coordinates.longitude.toFixed(6)}`);
        }
      });
    } catch (error) {
      console.error('Error in reverse geocoding:', error);
      setFullAddress(`${coordinates.latitude.toFixed(6)}, ${coordinates.longitude.toFixed(6)}`);
    }
  };

  // Check if user has granted location permission
  const checkLocationPermission = async () => {
    if (!navigator.permissions) {
      return 'unknown';
    }
    
    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      return permission.state; // 'granted', 'denied', or 'prompt'
    } catch (error) {
      console.warn('Could not check geolocation permission:', error);
      return 'unknown';
    }
  };

  // Check GPS and device capabilities
  const checkDeviceCapabilities = async () => {
    const capabilities = {
      hasGeolocation: !!navigator.geolocation,
      hasPermissions: !!navigator.permissions,
      isSecure: window.location.protocol === 'https:' || window.location.hostname === 'localhost',
      userAgent: navigator.userAgent,
      platform: navigator.platform
    };
    
    // Check if this looks like a mobile device (better GPS usually)
    capabilities.isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    console.log('📱 Device capabilities:', capabilities);
    return capabilities;
  };

  // Enhanced geolocation with GPS status detection and continuous improvement
  const getCurrentLocation = async () => {
    console.log('🔍 Starting enhanced location detection with GPS optimization...');
    
    setIsLocating(true);
    setLocationError('🔄 Initializing location services...');
    
    // Check device capabilities
    const deviceCapabilities = await checkDeviceCapabilities();
    setCapabilities(deviceCapabilities);
    
    if (!deviceCapabilities.hasGeolocation) {
      setLocationError('❌ Geolocation is not supported by this browser.');
      setIsLocating(false);
      return;
    }
    
    // Check location permission
    const permissionState = await checkLocationPermission();
    console.log('📍 Location permission state:', permissionState);
    
    if (permissionState === 'denied') {
      setLocationError('❌ Location access denied. Please enable location permissions in your browser settings and refresh the page.');
      setIsLocating(false);
      return;
    }
    
    // Warn about non-secure context
    if (!deviceCapabilities.isSecure) {
      setLocationError('⚠️ For best accuracy, please use HTTPS. Location may be less precise on non-secure connections.');
    }
    
    console.log('🌐 Starting GPS-optimized location detection...');
    setLocationError('🎯 Attempting to access GPS for high accuracy...');
    
    // First, try to use watchPosition for continuous improvement
    let bestPosition = null;
    let watchAttempts = 0;
    const maxWatchAttempts = 5;
    let improvementFound = false;
    
    const watchOptions = {
      enableHighAccuracy: true,
      timeout: 8000,
      maximumAge: 0
    };
    
    const startWatchPosition = () => {
      return new Promise((resolve, reject) => {
        console.log('👀 Starting watchPosition for continuous GPS improvement...');
        setLocationError('📡 Accessing GPS... This may take a moment for accuracy.');
        
        const watchId = navigator.geolocation.watchPosition(
          (position) => {
            watchAttempts++;
            console.log(`📍 Watch attempt ${watchAttempts}:`, {
              lat: position.coords.latitude.toFixed(6),
              lng: position.coords.longitude.toFixed(6),
              accuracy: Math.round(position.coords.accuracy) + 'm',
              timestamp: new Date(position.timestamp).toLocaleTimeString()
            });
            
            // If this is our first position or a significantly better one
            if (!bestPosition || position.coords.accuracy < bestPosition.coords.accuracy * 0.8) {
              bestPosition = position;
              improvementFound = true;
              console.log(`🎯 New best accuracy: ${Math.round(position.coords.accuracy)}m`);
              
              // Update UI with improving accuracy
              if (position.coords.accuracy <= 100) {
                setLocationError(`🎯 Excellent GPS lock achieved! (±${Math.round(position.coords.accuracy)}m)`);
              } else if (position.coords.accuracy <= 500) {
                setLocationError(`📡 Good GPS signal (±${Math.round(position.coords.accuracy)}m) - still improving...`);
              } else if (position.coords.accuracy <= 2000) {
                setLocationError(`📡 Getting GPS fix (±${Math.round(position.coords.accuracy)}m) - please wait...`);
              } else {
                setLocationError(`📡 Weak GPS signal (±${Math.round(position.coords.accuracy/1000)}km) - trying to improve...`);
              }
            }
            
            // Stop watching if we get excellent accuracy or after max attempts
            if (position.coords.accuracy <= 50 || watchAttempts >= maxWatchAttempts) {
              navigator.geolocation.clearWatch(watchId);
              resolve(bestPosition);
            }
          },
          (error) => {
            navigator.geolocation.clearWatch(watchId);
            console.warn('❌ Watch position failed:', error);
            reject(error);
          },
          watchOptions
        );
        
        // Stop watching after 30 seconds maximum
        setTimeout(() => {
          navigator.geolocation.clearWatch(watchId);
          if (bestPosition) {
            console.log('⏰ Watch timeout - using best position found');
            resolve(bestPosition);
          } else {
            reject(new Error('Watch position timeout with no results'));
          }
        }, 30000);
      });
    };
    
    try {
      // Try watchPosition first for best results
      const watchResult = await startWatchPosition();
      
      if (watchResult && watchResult.coords.accuracy <= 1000) {
        console.log('✅ Watch position successful with good accuracy');
        handleLocationSuccess(watchResult);
        return;
      }
    } catch (error) {
      console.warn('⚠️ Watch position failed, falling back to single requests:', error);
    }
    
    // Fallback to multiple single requests if watch position failed
    console.log('🔄 Falling back to multiple single location requests...');
    setLocationError('🔄 GPS not available, trying alternative methods...');
    
    let singleRequestBest = bestPosition; // Keep any result from watch
    const maxSingleAttempts = 3;
    
    for (let i = 1; i <= maxSingleAttempts; i++) {
      try {
        console.log(`📍 Single request attempt ${i}/${maxSingleAttempts}`);
        
        const singleOptions = {
          enableHighAccuracy: i <= 2, // First 2 attempts use high accuracy
          timeout: 10000,
          maximumAge: i === 1 ? 0 : 30000 // First attempt gets fresh data
        };
        
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, singleOptions);
        });
        
        console.log(`📊 Single attempt ${i} result:`, {
          lat: position.coords.latitude.toFixed(6),
          lng: position.coords.longitude.toFixed(6),
          accuracy: Math.round(position.coords.accuracy) + 'm'
        });
        
        if (!singleRequestBest || position.coords.accuracy < singleRequestBest.coords.accuracy) {
          singleRequestBest = position;
          console.log(`🏆 New best single request: ${Math.round(position.coords.accuracy)}m`);
        }
        
        // If we get decent accuracy, use it
        if (position.coords.accuracy <= 500) {
          break;
        }
        
      } catch (error) {
        console.warn(`❌ Single request ${i} failed:`, error);
      }
    }
    
    // Use the best position we found from either method
    if (singleRequestBest) {
      console.log('🏆 Using best position from all attempts:', {
        accuracy: Math.round(singleRequestBest.coords.accuracy) + 'm',
        source: improvementFound ? 'watch + single' : 'single requests only'
      });
      
      // Special handling for very poor accuracy
      if (singleRequestBest.coords.accuracy > 50000) {
        setLocationError('🚨 GPS appears to be disabled or unavailable. This looks like IP-based location. Please enable GPS on your device and try again.');
      }
      
      handleLocationSuccess(singleRequestBest);
    } else {
      // Final fallback - try network-based location
      console.log('🌐 All GPS attempts failed, trying network-based location...');
      setLocationError('📡 GPS unavailable, trying network-based location...');
      
      try {
        const networkPosition = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            {
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: 300000 // Accept 5-minute cached network location
            }
          );
        });
        
        console.log('📡 Network location result:', {
          lat: networkPosition.coords.latitude.toFixed(6),
          lng: networkPosition.coords.longitude.toFixed(6),
          accuracy: Math.round(networkPosition.coords.accuracy) + 'm'
        });
        
        setLocationError('📡 Using network-based location. For GPS accuracy, please enable location services and try again.');
        handleLocationSuccess(networkPosition);
        
      } catch (finalError) {
        console.error('❌ All location methods failed:', finalError);
        handleLocationError(finalError);
      }
    }
  };

  // Handle successful location with better accuracy validation
  const handleLocationSuccess = async (position) => {
    try {
      const { latitude, longitude, accuracy } = position.coords;
      
      console.log('📊 Processing location data:', {
        latitude: latitude.toFixed(6),
        longitude: longitude.toFixed(6),
        accuracy: Math.round(accuracy) + 'm',
        timestamp: new Date(position.timestamp).toLocaleTimeString()
      });
      
      // Validate coordinates
      if (!latitude || !longitude || 
          Math.abs(latitude) > 90 || 
          Math.abs(longitude) > 180 ||
          isNaN(latitude) || 
          isNaN(longitude)) {
        console.error('❌ Invalid coordinates:', { latitude, longitude });
        setLocationError('❌ Invalid location data received. Please try again.');
        setIsLocating(false);
        return;
      }
      
      // Check if location seems reasonable for India
      const isInIndia = latitude >= 6 && latitude <= 37 && longitude >= 68 && longitude <= 97;
      const isInBangalore = latitude >= 12.8 && latitude <= 13.2 && longitude >= 77.3 && longitude <= 77.8;
      
      if (!isInIndia) {
        console.warn('⚠️ Location appears to be outside India:', { latitude, longitude });
        setLocationError(`⚠️ Location detected outside India (${latitude.toFixed(4)}, ${longitude.toFixed(4)}). Please verify this is correct or try again.`);
      }
      
      // Create location object
      const newLocation = { latitude, longitude };
      setLocation(newLocation);
      
      // Update map if available
      if (mapInstance.current && googleMapsApi) {
        const mapPosition = new googleMapsApi.LatLng(latitude, longitude);
        mapInstance.current.setCenter(mapPosition);
        
        // Set zoom based on accuracy - more zoom for better accuracy
        let zoomLevel = 16;
        if (accuracy > 10000) {
          zoomLevel = 10; // City level for very poor accuracy (10km+)
        } else if (accuracy > 5000) {
          zoomLevel = 12; // District level (5-10km)
        } else if (accuracy > 2000) {
          zoomLevel = 13; // Area level (2-5km)
        } else if (accuracy > 1000) {
          zoomLevel = 14; // Large neighborhood (1-2km)
        } else if (accuracy > 500) {
          zoomLevel = 15; // Neighborhood level (500m-1km)
        } else if (accuracy > 100) {
          zoomLevel = 16; // Street level (100-500m)
        } else {
          zoomLevel = 17; // Building level for very accurate locations (<100m)
        }
        
        mapInstance.current.setZoom(zoomLevel);
        updateMarkerPosition(mapPosition, mapInstance.current);
      }
      
      // Clear errors
      setErrors(prev => ({ ...prev, location: '' }));
      
      // Provide detailed feedback about location accuracy
      let feedbackMessage = '';
      let feedbackClass = '';
      
      if (accuracy <= 50) {
        feedbackMessage = `🎯 Excellent location accuracy (±${Math.round(accuracy)}m)! Location is very precise.`;
        feedbackClass = 'bg-green-50 text-green-700 border-green-200';
      } else if (accuracy <= 200) {
        feedbackMessage = `✅ Good location accuracy (±${Math.round(accuracy)}m). Location should be quite accurate.`;
        feedbackClass = 'bg-green-50 text-green-700 border-green-200';
      } else if (accuracy <= 1000) {
        feedbackMessage = `⚠️ Moderate accuracy (±${Math.round(accuracy)}m). You may want to drag the marker to your exact location.`;
        feedbackClass = 'bg-yellow-50 text-yellow-700 border-yellow-200';
      } else if (accuracy <= 5000) {
        feedbackMessage = `⚠️ Low accuracy (±${Math.round(accuracy/1000)}km). Please drag the marker to your exact location on the map.`;
        feedbackClass = 'bg-orange-50 text-orange-700 border-orange-200';
      } else {
        feedbackMessage = `❌ Very poor accuracy (±${Math.round(accuracy/1000)}km). GPS may be disabled. Please manually place the marker or enable GPS and try again.`;
        feedbackClass = 'bg-red-50 text-red-700 border-red-200';
      }
      
      // Add location context
      if (isInBangalore) {
        feedbackMessage += ' 📍 Detected in Bangalore area.';
      } else if (isInIndia) {
        feedbackMessage += ' 📍 Detected in India.';
      }
      
      setLocationError(feedbackMessage);
      
      // Store accuracy class for styling
      setLocationError(feedbackMessage);
      
      // Clear feedback message after appropriate time based on accuracy
      const clearTime = accuracy > 1000 ? 10000 : 5000;
      setTimeout(() => {
        setLocationError('');
      }, clearTime);
      
      // Reverse geocode to get address
      await reverseGeocode(newLocation);
      
      setIsLocating(false);
      console.log('✅ Location process completed successfully');
      
    } catch (error) {
      console.error('❌ Error processing location:', error);
      setLocationError('❌ Error processing your location. Please try again.');
      setIsLocating(false);
    }
  };

  // Handle location errors with specific messages
  const handleLocationError = (error) => {
    console.error('❌ Geolocation error:', error);
    
    let errorMessage = '';
    let suggestions = '';
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = '❌ Location access denied.';
        suggestions = 'Please click the location icon in your browser\'s address bar and allow location access, then try again.';
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = '❌ Your location is currently unavailable.';
        suggestions = 'Please check your internet connection and try again, or enter your address manually.';
        break;
      case error.TIMEOUT:
        errorMessage = '⏰ Location request timed out.';
        suggestions = 'Please try again or enter your address manually.';
        break;
      default:
        errorMessage = '❌ An error occurred while getting your location.';
        suggestions = 'Please try again or enter your address manually.';
        break;
    }
    
    setLocationError(`${errorMessage} ${suggestions}`);
    setIsLocating(false);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!street.trim()) newErrors.street = 'Street address is required';
    if (!city.trim()) newErrors.city = 'City is required';
    if (!state.trim()) newErrors.state = 'State is required';
    if (!zip.trim()) newErrors.zip = 'ZIP/Postal code is required';
    if (!location) newErrors.location = 'Please select your location on the map or use current location';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      updateData({
        street,
        city,
        state,
        zip,
        location,
        fullAddress,
        address: `${street}, ${city}, ${state} ${zip}`
      });
      nextStep();
    }
  };
  
  // For demo purposes, simulate map selection
  const simulateMapSelection = () => {
    const demoLocation = { 
      latitude: 12.9716, 
      longitude: 77.5946 // Bangalore coordinates
    };
    
    setLocation(demoLocation);
    setFullAddress("123 MG Road, Bangalore, Karnataka 560001");
    setStreet("123 MG Road");
    setCity("Bangalore");
    setState("Karnataka");
    setZip("560001");
    
    if (autocompleteRef.current) {
      autocompleteRef.current.value = "123 MG Road, Bangalore, Karnataka 560001";
    }
    
    // Update map if available
    if (mapInstance.current && googleMapsApi) {
      const position = new googleMapsApi.LatLng(demoLocation.latitude, demoLocation.longitude);
      mapInstance.current.setCenter(position);
      updateMarkerPosition(position, mapInstance.current);
    }
    
    setLocationError('');
    setErrors(prev => ({ ...prev, location: '' }));
  };
  
  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-4">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Shop Address Information
      </h3>
      
      {/* Location Options */}
      <div className="mb-4 p-4 bg-gray-50 rounded-md">
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            type="button" 
            variant="secondary"
            onClick={getCurrentLocation}
            disabled={isLocating}
            className="flex items-center justify-center gap-2"
          >
            {isLocating ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                Getting Location...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Use Current Location
              </>
            )}
          </Button>
          
          <Button 
            type="button" 
            variant="secondary"
            onClick={simulateMapSelection}
            className="flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3" />
            </svg>
            Demo Location (Bangalore)
          </Button>
        </div>
        
        {/* Show location error/success message */}
        {locationError && (
          <div className={`mt-3 p-3 rounded-md text-sm border ${
            locationError.includes('🎯') || locationError.includes('✅') 
              ? 'bg-green-50 text-green-700 border-green-200' 
              : locationError.includes('⚠️') || locationError.includes('📡')
              ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
              : locationError.includes('❌')
              ? 'bg-red-50 text-red-700 border-red-200'
              : 'bg-blue-50 text-blue-700 border-blue-200'
          }`}>
            <div className="flex items-start">
              <div className="flex-1">
                {locationError}
                {locationError.includes('GPS may be disabled') && (
                  <div className="mt-2 text-xs">
                    <strong>💡 Tips for better accuracy:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li><strong>Enable GPS/Location Services</strong> in your device settings</li>
                      <li><strong>Allow precise location</strong> in browser settings (not just approximate)</li>
                      <li><strong>Move outside or near a window</strong> for better GPS signal</li>
                      <li><strong>Wait 30-60 seconds</strong> for GPS to get a better fix</li>
                      <li><strong>Refresh the page</strong> and try again after enabling GPS</li>
                      <li><strong>Use the map to manually</strong> adjust your exact location</li>
                    </ul>
                    {capabilities && capabilities.isMobile && (
                      <div className="mt-2 p-2 bg-blue-50 rounded text-blue-700">
                        📱 <strong>Mobile Device Detected:</strong> Please ensure Location Services are enabled in your device settings, then refresh this page.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Google Maps Autocomplete */}
      <div className="mb-4">
        <label 
          htmlFor="autocomplete" 
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Search Address
        </label>
        <input
          ref={autocompleteRef}
          id="autocomplete"
          type="text"
          placeholder="Start typing your address..."
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm py-2 px-3 border"
          defaultValue={fullAddress}
        />
        <p className="text-xs text-gray-500 mt-1">
          Start typing and select from the dropdown suggestions
        </p>
      </div>
      
      {/* Map */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Location on Map
        </label>
        <div 
          ref={mapRef} 
          className="w-full h-80 bg-gray-200 rounded-md border"
        >
          {/* This div will be replaced by Google Map */}
          {!mapLoaded && (
            <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mb-2"></div>
              <p className="text-gray-500 mb-1">Loading interactive map...</p>
              <p className="text-xs text-gray-400">Please wait while we load Google Maps</p>
            </div>
          )}
        </div>
        {errors.location && (
          <p className="mt-1 text-sm text-red-600">
            {errors.location}
          </p>
        )}
      </div>
      
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Street Address"
          id="street"
          name="street"
          type="text"
          value={street}
          onChange={(e) => setStreet(e.target.value)}
          error={errors.street}
          placeholder="Street address"
          required
        />
        
        <Input
          label="City"
          id="city"
          name="city"
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          error={errors.city}
          placeholder="City"
          required
        />
      </div>
      
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="State"
          id="state"
          name="state"
          type="text"
          value={state}
          onChange={(e) => setState(e.target.value)}
          error={errors.state}
          placeholder="State/Province"
          required
        />
        
        <Input
          label="ZIP / Postal Code"
          id="zip"
          name="zip"
          type="text"
          value={zip}
          onChange={(e) => setZip(e.target.value)}
          error={errors.zip}
          placeholder="ZIP/Postal code"
          required
        />
      </div>
      
      {location && (
        <div className="text-sm text-gray-600 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <div>
              <p className="font-medium text-green-700 mb-1">✓ Location Selected</p>
              <p className="text-sm">Latitude: {location.latitude.toFixed(6)}</p>
              <p className="text-sm">Longitude: {location.longitude.toFixed(6)}</p>
              {fullAddress && (
                <p className="text-sm mt-1 font-medium">{fullAddress}</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="flex justify-between mt-6">
        <Button type="button" variant="secondary" onClick={prevStep}>
          Previous
        </Button>
        <Button type="submit" variant="primary">
          Next
        </Button>
      </div>
    </form>
  );
};

export default AddressStep;